"use client";

import globalImg from "@/_assets/svgs/globalMap.svg";
import { cleanString } from "@/helpers/cleanString";
import { isSearchQueryMatch } from "@/helpers/isSearchQueryMatch";
import { getGtmUserData } from "@/helpers/getGtmUserData";
import { sendGTMEvent } from "@/helpers/sendGTMEvent";
import { cn } from "@/lib/utils";
import { SearchPackagesListReturn } from "@/types/packages/data-only/SearchPackagesList";
import { Search } from "lucide-react";
import { usePathname } from "@/i18n/navigation";
import { useEffect, useMemo, useRef, useState } from "react";
import { Input } from "../../ui/input";
import CountryItem from "./CountryItem";
import { useTranslations } from "next-intl";
import { useAuth } from "@/providers/AuthProvider";
import { Spinner } from "@/components/my-ui/fallbacks/Spinner";

interface PropsType {
  searchInputStyle?: string;
  packagesList: SearchPackagesListReturn | null;
  topDestinations: React.ReactNode;
  isSearchLoading: boolean;
  setShowDialog: React.Dispatch<React.SetStateAction<boolean>>;
  isDataVoice?: boolean;
}

function CountryRegionSearch({
  searchInputStyle,
  packagesList,
  topDestinations,
  isSearchLoading,
  setShowDialog,
  isDataVoice,
}: PropsType) {
  const [searchQuery, setSearchQuery] = useState("");
  const [showSuggestions, setShowSuggestions] = useState(false);
  const containerRef = useRef<HTMLDivElement | null>(null);
  const pathName = usePathname();
  const inputRef = useRef<HTMLInputElement | null>(null);
  const t = useTranslations("Shared.countrySearch");
  const { user, isAuthLoading } = useAuth();

  // routes where Data/Voice/SMS takes precedence
  const dataVoiceLinks = [
    "/data-voice-sms/",
    "/data-voice-sms/regional/",
  ];
  const isDataVoicePage =
    dataVoiceLinks.includes(pathName) ||
    (pathName === "/global/" && isDataVoice === true);

  // --- shared: click-outside to close dropdown
  useEffect(() => {
    function handleClickOutside(event: MouseEvent) {
      if (
        containerRef.current &&
        !containerRef.current.contains(event.target as Node)
      ) {
        setShowSuggestions(false);
      }
    }
    document.addEventListener("mousedown", handleClickOutside);
    return () => document.removeEventListener("mousedown", handleClickOutside);
  }, []);

  useEffect(() => {
    const timer = setTimeout(() => {
      inputRef.current?.focus();
      inputRef.current?.scrollIntoView({ block: "center" });
    }, 400); // small delay to wait for Drawer animation
    return () => clearTimeout(timer);
  }, []);

  // --- input change (safe while loading; does not trigger filtering while loading)
  function handleSearchQuery(e: React.ChangeEvent<HTMLInputElement>) {
    const query = cleanString(e.target.value);
    setSearchQuery(query);
    if (query) setShowSuggestions(true);
  }

  // --- do not run GTM if search query is empty
  useEffect(() => {
    if (isAuthLoading || !cleanString(searchQuery)) return;

    const timeout = setTimeout(() => {
      sendGTMEvent({
        event: "search",
        search_term: searchQuery,
        user_data: getGtmUserData(user?.name),
      });
    }, 1000);

    return () => clearTimeout(timeout);
  }, [searchQuery, isAuthLoading, user?.name]);

  // --- build filtered results only when there is a query
  const filteredPackagesList = useMemo(() => {
    if (!packagesList) return null;
    if (!searchQuery) return null;

    const {
      local,
      regional,
      global,
      local_voice,
      regional_voice,
      global_voice,
    } = packagesList;

    return {
      dataOnly: {
        local: local.filter((country) =>
          isSearchQueryMatch({ country, searchQuery }),
        ),
        regional: regional.filter((region) =>
          isSearchQueryMatch({ region, searchQuery }),
        ),
        global: {
          href: global.href,
          countries: global.countries.filter((country) =>
            isSearchQueryMatch({ country, searchQuery, isGlobal: true }),
          ),
        },
      },
      dataVoice: {
        local: local_voice.filter((country) =>
          isSearchQueryMatch({ country, searchQuery }),
        ),
        regional: regional_voice.filter((region) =>
          isSearchQueryMatch({ region, searchQuery }),
        ),
        global: {
          href: global_voice.href,
          countries: global_voice.countries.filter((country) =>
            isSearchQueryMatch({ country, searchQuery, isGlobal: true }),
          ),
        },
      },
    };
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [searchQuery, packagesList]);

  // --- derive flags safely from filtered results
  const flags = useMemo(() => {
    if (!filteredPackagesList) {
      return {
        isPackages: false,
        isDataOnlyPackages: false,
        isDataVoicePackages: false,
        isDataOnlyLocal: false,
        isDataOnlyRegional: false,
        isDataOnlyGlobal: false,
        isDataVoiceLocal: false,
        isDataVoiceRegional: false,
        isDataVoiceGlobal: false,
      };
    }

    const { dataOnly, dataVoice } = filteredPackagesList;
    const { global, regional, local } = dataOnly;
    const {
      global: globalVoice,
      regional: regionalVoice,
      local: localVoice,
    } = dataVoice;

    const isDataOnlyPackages =
      local.length > 0 || regional.length > 0 || global.countries.length > 0;

    const isDataVoicePackages =
      localVoice.length > 0 ||
      regionalVoice.length > 0 ||
      globalVoice.countries.length > 0;

    const isPackages = isDataVoicePage
      ? isDataVoicePackages
      : isDataOnlyPackages || isDataVoicePackages;

    return {
      isPackages,
      isDataOnlyPackages,
      isDataVoicePackages,
      isDataOnlyLocal: isDataOnlyPackages && local.length > 0,
      isDataOnlyRegional: isDataOnlyPackages && regional.length > 0,
      isDataOnlyGlobal: isDataOnlyPackages && global.countries.length > 0,
      isDataVoiceLocal: isDataVoicePackages && localVoice.length > 0,
      // fixed: these should check isDataVoicePackages, not isDataOnlyPackages
      isDataVoiceRegional: isDataVoicePackages && regionalVoice.length > 0,
      isDataVoiceGlobal:
        isDataVoicePackages && globalVoice.countries.length > 0,
    };
  }, [filteredPackagesList, isDataVoicePage]);

  return (
    <div
      ref={containerRef}
      className="relative z-50 grid w-full grid-rows-[auto_1fr] gap-2"
    >
      {/* Search Input */}
      <div className="relative w-full">
        <Input
          placeholder={t("inputPlaceholder")}
          className={cn("!rounded-lg", searchInputStyle)}
          onChange={handleSearchQuery}
          value={searchQuery}
          onFocus={() => {
            setShowSuggestions(true);
            // ensure input stays visible
            setTimeout(() => {
              inputRef.current?.scrollIntoView({
                block: "center",
                behavior: "smooth",
              });
            }, 300);
          }}
          aria-expanded={showSuggestions}
          aria-haspopup="listbox"
          ref={inputRef}
          // autoFocus
          inputMode="text"
        />

        <Search className="absolute end-3 top-1/2 -translate-y-1/2 transform text-primary" />
      </div>

      {/* Dropdown */}
      <div className="barMini max-h-[80dvh] min-h-[80dvh] overflow-y-auto rounded-md bg-background py-4 md:max-h-[350px] lg:min-h-[350px]">
        {searchQuery && isSearchLoading ? (
          <div className="flex min-h-[200px] items-center justify-center">
            <Spinner />
          </div>
        ) : flags.isPackages ? (
          <div className="flex flex-col gap-4">
            {/* Data Only */}
            {!isDataVoicePage && flags.isDataOnlyPackages && (
              <div>
                <p className="mb-2 ps-2 font-montserrat text-[16px] font-semibold leading-none text-primary">
                  {t(`dataOnly`)}
                </p>

                {flags.isDataOnlyLocal && (
                  <div>
                    {filteredPackagesList?.dataOnly.local.map((item, index) => (
                      <CountryItem
                        countryName={item.name}
                        image_url={item.image_url}
                        index={index}
                        href={item.href}
                        key={`do-local-${index}-${item.name}`}
                        setShowDialog={setShowDialog}
                      />
                    ))}
                  </div>
                )}

                {flags.isDataOnlyRegional && (
                  <div>
                    {filteredPackagesList?.dataOnly.regional.map(
                      (item, index) => (
                        <CountryItem
                          countryName={item.name}
                          image_url={item.image_url}
                          index={index}
                          href={item.href}
                          key={`do-reg-${index}-${item.name}`}
                          setShowDialog={setShowDialog}
                        />
                      ),
                    )}
                  </div>
                )}

                {flags.isDataOnlyGlobal && (
                  <div>
                    <CountryItem
                      countryName="Global"
                      image_url={globalImg}
                      index={1}
                      href="/global/"
                      setShowDialog={setShowDialog}
                    />
                  </div>
                )}
              </div>
            )}

            {/* Data / Voice / SMS */}
            {flags.isDataVoicePackages && (
              <div>
                <p className="mb-2 ps-2 font-montserrat text-[16px] font-semibold leading-none text-primary">
                  {t(`dataVoiceSms`)}
                </p>

                {flags.isDataVoiceLocal && (
                  <div>
                    {filteredPackagesList?.dataVoice.local.map(
                      (item, index) => (
                        <CountryItem
                          countryName={item.name}
                          image_url={item.image_url}
                          index={index}
                          href={item.href}
                          key={`dv-local-${index}-${item.name}`}
                          setShowDialog={setShowDialog}
                        />
                      ),
                    )}
                  </div>
                )}

                {flags.isDataVoiceRegional && (
                  <div>
                    {filteredPackagesList?.dataVoice.regional.map(
                      (item, index) => (
                        <CountryItem
                          countryName={item.name}
                          image_url={item.image_url}
                          index={index}
                          href={item.href}
                          key={`dv-reg-${index}-${item.name}`}
                          setShowDialog={setShowDialog}
                        />
                      ),
                    )}
                  </div>
                )}

                {flags.isDataVoiceGlobal && (
                  <div>
                    <CountryItem
                      countryName="Global"
                      image_url={globalImg}
                      index={0}
                      href="/global/?package_type=data-voice"
                      setShowDialog={setShowDialog}
                    />
                  </div>
                )}
              </div>
            )}
          </div>
        ) : (
          // No matches => show Top Destinations
          <div>
            {searchQuery && (
              <p className="mb-2 ps-2 text-sm">
                {t(`topDestinations.noMatch`)}
              </p>
            )}

            <div
              onClickCapture={(event) => {
                const target = event.target as HTMLElement;

                if (target.closest("a, button")) {
                  setShowDialog(false);
                }
              }}
            >
              {topDestinations}
            </div>
          </div>
        )}
      </div>
    </div>
  );
}

export default CountryRegionSearch;
